import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useWorkflowUtilityPanels, UTILITY_PANELS } from '../../hooks/useWorkflowUtilityPanels';
import { createStickyNote } from '../../utils/workflowBuilderUtils';
import WorkflowUtilityRail from './WorkflowUtilityRail';
import WorkflowCanvasViewport from './WorkflowCanvasViewport';
import WorkflowCanvasStickyNotes from './WorkflowCanvasStickyNotes';
import WorkflowStickyNotesPanel from './WorkflowStickyNotesPanel';
import WorkflowSwitcherPanel from './WorkflowSwitcherPanel';
import WorkflowVersionHistoryPanel from './WorkflowVersionHistoryPanel';
import WorkflowAIPanel from './WorkflowAIPanel';
import WorkflowSearchPanel from './WorkflowSearchPanel';
import WorkflowValidationPanel from './WorkflowValidationPanel';
import WorkflowCommentsPanel from './WorkflowCommentsPanel';
import WorkflowBuilderBodyLayout from './WorkflowBuilderBodyLayout';

const WorkflowBuilderUtilityShell = ({
  enabled = true,
  children,
  workflow,
  setWorkflow,
  workflowId,
  token,
  apiBase,
  systemApiBase,
  builderPathPrefix = '/platform/automations/workflows',
  onUnsavedChange,
  onWorkflowReload,
  className,
  contentClassName,
  variant = 'agency',
  editorPanel = null,
  editorPanelWidth = 0,
  editorOverlayMode = false,
  onEditorOverlayDismiss,
  executionPathView = null,
  onExitExecutionPath,
}) => {
  const navigate = useNavigate();
  const {
    activePanel,
    statsViewActive,
    stickyNotesVisible,
    setStickyNotesVisible,
    stickyPaletteOpen,
    highlightedStepId,
    setHighlightedStepId,
    togglePanel,
    closePanel,
  } = useWorkflowUtilityPanels();

  const isPathViewActive = Boolean(executionPathView);
  const isCanvasReadOnly = statsViewActive || isPathViewActive;
  const canvasNotes = workflow?.canvas_notes || [];

  const pathStepStates = React.useMemo(() => {
    const map = {};
    (executionPathView?.steps || []).forEach((step) => {
      if (step.action_id) map[step.action_id] = step.state;
    });
    return map;
  }, [executionPathView]);

  const updateCanvasNotes = useCallback(
    (notes) => {
      setWorkflow((prev) => ({ ...prev, canvas_notes: notes }));
      onUnsavedChange?.();
    },
    [setWorkflow, onUnsavedChange],
  );

  const handleAddStickyNote = useCallback(
    (color) => {
      const offset = canvasNotes.length * 24;
      updateCanvasNotes([...canvasNotes, createStickyNote(color, 100 + offset, 100 + offset)]);
    },
    [canvasNotes, updateCanvasNotes],
  );

  const handleApplyAiDraft = useCallback(
    (draft) => {
      setWorkflow((prev) => ({
        ...prev,
        name: draft.name || prev.name,
        description: draft.description || prev.description,
        category: draft.category || prev.category,
        trigger: draft.trigger_event
          ? { id: draft.trigger_event, name: draft.trigger_event.replace(/[._]/g, ' ') }
          : prev.trigger,
        steps: draft.steps || prev.steps,
      }));
      onUnsavedChange?.();
    },
    [setWorkflow, onUnsavedChange],
  );

  const handleAddComment = useCallback(
    (text) => {
      const comment = {
        id: `comment_${Date.now()}`,
        text,
        author: 'You',
        created_at: new Date().toISOString(),
      };
      setWorkflow((prev) => ({
        ...prev,
        comments: [...(prev.comments || []), comment],
      }));
      onUnsavedChange?.();
    },
    [setWorkflow, onUnsavedChange],
  );

  useEffect(() => {
    if (!highlightedStepId) return undefined;
    const el = document.querySelector(`[data-testid="step-node-${highlightedStepId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const timer = setTimeout(() => setHighlightedStepId(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightedStepId, setHighlightedStepId]);

  if (!enabled) {
    return <div className={className}>{children}</div>;
  }

  const renderUtilityPanel = () => {
    switch (activePanel) {
      case UTILITY_PANELS.switcher:
        return (
          <WorkflowSwitcherPanel
            apiBase={apiBase}
            token={token}
            currentWorkflowId={workflowId}
            currentWorkflow={workflow}
            builderPathPrefix={builderPathPrefix}
            onClose={closePanel}
            onNavigate={(id) => {
              closePanel();
              if (id === 'new') {
                navigate(`${builderPathPrefix}/new/builder`);
              } else {
                navigate(`${builderPathPrefix}/${id}/builder`);
              }
            }}
            onCreate={() => {
              closePanel();
              navigate(`${builderPathPrefix}/new/builder`);
            }}
          />
        );
      case UTILITY_PANELS.versions:
        return (
          <WorkflowVersionHistoryPanel
            apiBase={apiBase}
            token={token}
            workflowId={workflowId}
            onClose={closePanel}
            onRestored={onWorkflowReload}
          />
        );
      case UTILITY_PANELS.ai:
        return (
          <WorkflowAIPanel
            apiBase={systemApiBase || apiBase}
            token={token}
            workflow={workflow}
            onClose={closePanel}
            onApplyDraft={handleApplyAiDraft}
          />
        );
      case UTILITY_PANELS.search:
        return (
          <WorkflowSearchPanel
            steps={workflow?.steps || []}
            onClose={closePanel}
            onSelectResult={(stepId) => {
              setHighlightedStepId(stepId);
              closePanel();
            }}
          />
        );
      case UTILITY_PANELS.validation:
        return <WorkflowValidationPanel workflow={workflow} onClose={closePanel} />;
      case UTILITY_PANELS.comments:
        return (
          <WorkflowCommentsPanel
            comments={workflow?.comments || []}
            onClose={closePanel}
            onAddComment={handleAddComment}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={cn('flex min-h-0 flex-1', className)} data-testid="workflow-builder-utility-shell">
      <WorkflowUtilityRail
        activePanel={activePanel}
        statsViewActive={statsViewActive}
        stickyPaletteOpen={stickyPaletteOpen}
        onToggle={togglePanel}
      />

      <div className="relative flex min-h-0 min-w-0 flex-1">
        {stickyPaletteOpen && (
          <WorkflowStickyNotesPanel
            onSelectColor={handleAddStickyNote}
            notesVisible={stickyNotesVisible}
            onHideNotes={() => setStickyNotesVisible((v) => !v)}
          />
        )}

        {activePanel && (
          <div className="z-40 shrink-0 pl-3 pt-3">
            {renderUtilityPanel()}
          </div>
        )}

        <WorkflowBuilderBodyLayout
          variant={variant}
          editorPanel={editorPanel}
          editorPanelWidth={editorPanelWidth}
          editorOverlayMode={editorOverlayMode}
          onEditorOverlayDismiss={onEditorOverlayDismiss}
          workspace={(
            <div
              className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
              data-testid="workflow-canvas-layout"
              data-editor-panel-width={editorPanelWidth || 0}
              data-editor-overlay-mode={editorOverlayMode ? 'true' : 'false'}
            >
              <WorkflowCanvasViewport
                executionPathView={executionPathView}
                onExitExecutionPath={onExitExecutionPath}
                statsViewActive={statsViewActive}
                workflowStatus={workflow?.status}
                className="min-h-0 flex-1"
              >
                {(viewportApi) => (
                  <>
                    <WorkflowCanvasStickyNotes
                      notes={canvasNotes}
                      visible={stickyNotesVisible}
                      readOnly={isCanvasReadOnly}
                      onNotesChange={updateCanvasNotes}
                    />

                    <div
                      className={cn('relative flex w-full flex-col items-center space-y-4 p-6', contentClassName)}
                      data-workflow-graph-coordinate-content
                    >
                      {typeof children === 'function'
                        ? children({
                          isBuilderReadOnly: isCanvasReadOnly,
                          highlightedStepId,
                          statsViewActive,
                          executionPathView,
                          pathStepStates,
                          viewport: viewportApi,
                        })
                        : children}
                    </div>
                  </>
                )}
              </WorkflowCanvasViewport>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default WorkflowBuilderUtilityShell;
